/**
 * @fileoverview QueryClient factory with default configuration
 */
import { QueryClient } from '@tanstack/react-query';

import { PERSIST_MAX_AGE } from './persister';
import { GC_TIMES, STALE_TIMES } from './staleTime';

/**
 * Creates a configured QueryClient instance
 *
 * @returns Configured QueryClient for the application
 *
 * @example
 * // In App.tsx:
 * import { createQueryClient } from 'lib/query';
 * const queryClient = createQueryClient();
 */
export const createQueryClient = (): QueryClient => {
  // Ensure gcTime is at least as long as persistence maxAge
  const effectiveGcTime = Math.max(GC_TIMES.MEDIUM, PERSIST_MAX_AGE);

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIMES.MEDIUM,
        gcTime: effectiveGcTime, // Aligned with persistence
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Never retry auth errors - interceptor handles token refresh
          const status = (error as any)?.response?.status ?? (error as any)?.status;
          if (status === 401 || status === 403) return false;
          return failureCount < 1;
        },
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: (failureCount, error) => {
          const status = (error as any)?.response?.status ?? (error as any)?.status;
          if (status === 401 || status === 403) return false;
          return failureCount < 1;
        },
      },
    },
  });
};

/**
 * Singleton QueryClient instance
 * Use this for SSR or when you need the same client instance
 */
let queryClientInstance: QueryClient | null = null;

export const getQueryClient = (): QueryClient => {
  if (!queryClientInstance) {
    queryClientInstance = createQueryClient();
  }
  return queryClientInstance;
};
